import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createBillingProvider,
  deleteBillingProvider,
  listBillingProviders,
  updateBillingProvider,
} from '../api/claimsClient';
import { HEAD_CELL_SX, TABLE_CONTAINER_SX } from '../styles/tableStyles';

const EMPTY_PROFILE = {
  id: '',
  organization_name: '',
  first_name: '',
  last_name: '',
  npi: '',
  tax_id: '',
  tax_id_qualifier: 'EI',
  taxonomy_code: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
};

function providerDisplayName(profile) {
  return profile.organization_name || [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unnamed provider';
}

export default function ProvidersPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_PROFILE);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const sortedProfiles = useMemo(
    () => [...profiles].sort((a, b) => providerDisplayName(a).localeCompare(providerDisplayName(b))),
    [profiles],
  );

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setProfiles(await listBillingProviders());
    } catch (_err) {
      setError('Provider profiles could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const openNew = () => {
    setDraft(EMPTY_PROFILE);
    setEditorOpen(true);
  };

  const openEdit = (profile) => {
    setDraft({ ...EMPTY_PROFILE, ...profile });
    setEditorOpen(true);
  };

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const saveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      const saved = draft.id
        ? await updateBillingProvider(draft.id, draft)
        : await createBillingProvider(draft);
      setProfiles((current) => {
        const exists = current.some((profile) => profile.id === saved.id);
        return exists
          ? current.map((profile) => (profile.id === saved.id ? saved : profile))
          : [...current, saved];
      });
      setEditorOpen(false);
    } catch (_err) {
      setError('Provider profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const removeProfile = async (profile) => {
    if (!window.confirm(`Delete ${providerDisplayName(profile)}? Existing claims will keep their copied provider fields.`)) {
      return;
    }
    setDeletingId(profile.id);
    setError('');
    try {
      await deleteBillingProvider(profile.id);
      setProfiles((current) => current.filter((item) => item.id !== profile.id));
    } catch (_err) {
      setError('Provider profile could not be deleted.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Providers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage billing provider profiles used during claim creation.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openNew}>
          New provider
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={TABLE_CONTAINER_SX}>
        <Table size="small" aria-label="Provider profiles">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>Provider</TableCell>
              <TableCell sx={HEAD_CELL_SX}>NPI</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Tax ID</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Taxonomy</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Phone</TableCell>
              <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6}>Loading providers...</TableCell>
              </TableRow>
            )}
            {!loading && sortedProfiles.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No provider profiles yet.</TableCell>
              </TableRow>
            )}
            {!loading && sortedProfiles.map((profile) => (
              <TableRow key={profile.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{providerDisplayName(profile)}</Typography>
                  {(profile.address_line_1 || profile.city || profile.state) && (
                    <Typography variant="caption" color="text.secondary">
                      {[profile.address_line_1, profile.city, profile.state].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{profile.npi || '-'}</TableCell>
                <TableCell>{profile.tax_id || '-'}</TableCell>
                <TableCell>{profile.taxonomy_code || '-'}</TableCell>
                <TableCell>{profile.phone || '-'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" justifyContent="flex-end" gap={1}>
                    <Button size="small" variant="outlined" onClick={() => openEdit(profile)}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={deletingId === profile.id}
                      onClick={() => removeProfile(profile)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{draft.id ? 'Edit provider' : 'New provider'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
            <TextField
              label="Organization name"
              value={draft.organization_name}
              onChange={(event) => setField('organization_name', event.target.value)}
              fullWidth
            />
            <TextField
              label="NPI"
              value={draft.npi}
              onChange={(event) => setField('npi', event.target.value)}
              inputProps={{ maxLength: 10 }}
              fullWidth
            />
            <TextField
              label="First name"
              value={draft.first_name}
              onChange={(event) => setField('first_name', event.target.value)}
              fullWidth
            />
            <TextField
              label="Last name"
              value={draft.last_name}
              onChange={(event) => setField('last_name', event.target.value)}
              fullWidth
            />
            <TextField
              label="Tax ID"
              value={draft.tax_id}
              onChange={(event) => setField('tax_id', event.target.value)}
              fullWidth
            />
            <TextField
              label="Tax ID qualifier"
              select
              SelectProps={{ native: true }}
              value={draft.tax_id_qualifier}
              onChange={(event) => setField('tax_id_qualifier', event.target.value)}
              fullWidth
            >
              <option value="EI">EIN</option>
              <option value="SY">SSN</option>
            </TextField>
            <TextField
              label="Taxonomy code"
              value={draft.taxonomy_code}
              onChange={(event) => setField('taxonomy_code', event.target.value)}
              fullWidth
            />
            <TextField
              label="Phone"
              value={draft.phone}
              onChange={(event) => setField('phone', event.target.value)}
              fullWidth
            />
            <TextField
              label="Address line 1"
              value={draft.address_line_1}
              onChange={(event) => setField('address_line_1', event.target.value)}
              fullWidth
            />
            <TextField
              label="Address line 2"
              value={draft.address_line_2}
              onChange={(event) => setField('address_line_2', event.target.value)}
              fullWidth
            />
            <TextField
              label="City"
              value={draft.city}
              onChange={(event) => setField('city', event.target.value)}
              fullWidth
            />
            <TextField
              label="State"
              value={draft.state}
              onChange={(event) => setField('state', event.target.value.toUpperCase())}
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
            <TextField
              label="Zip code"
              value={draft.zip_code}
              onChange={(event) => setField('zip_code', event.target.value)}
              inputProps={{ maxLength: 10 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveProfile} disabled={saving}>
            {saving ? 'Saving...' : 'Save provider'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
